import {
  AspectRatio,
  Box,
  Flex,
  Image as ChakraImage,
  Link,
  Stack,
  Switch,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import {
  BackofficeGroupPromoListItem,
  BackofficePromoQuery,
  BackofficeUpdateProductHighlightNumberBody,
} from '@diamond/shared/types';
import { showToast, Text } from '@diamond/shared/ui';
import { promoValidFromToFormat } from '@diamond/shared/utils';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';

import {
  BACKOFFICE_GROUP_PROMO_QUERY_KEY,
  toggleShowPromoHomepage,
  updatePromoDetail,
} from '../api';
import { updateHighlightPromo } from '../api/update-highlight-promo';
import { columnWidths } from './columns-width';
import { ProductHighlightInput } from './product/product-highlight-input';

export interface PromoItemProps {
  item: BackofficeGroupPromoListItem;
  customer_type: string | undefined;
}

export function PromoItem(props: PromoItemProps) {
  const token = useAuthStore((s) => s.access_token);
  const toast = useToast();
  const queryClient = useQueryClient();
  const { control } = useForm({
    defaultValues: {
      [`highlight-${props.item.id}`]: props.item.product_highlight_number || 0,
    },
  });

  const isDisabled = !props.customer_type;
  const detailLink = props.customer_type
    ? `/promo/${props.item.id}?customer_type=${props.customer_type}`
    : `/promo/${props.item.id}`;

  const handleUpdateHighlight = useMutation({
    mutationFn: async (newHighlightNumber: number) => {
      const payload = {
        product_highlight_number: newHighlightNumber,
        customer_type: props.customer_type,
      };
      await updateHighlightPromo(token, props.item.id, payload);
    },
    onSuccess: () => {
      showToast(toast, 'success', 'Berhasil update Product Highlight');
      queryClient.invalidateQueries({
        queryKey: [BACKOFFICE_GROUP_PROMO_QUERY_KEY],
      });
    },
    onError: (err: AxiosError<{ message: string; statusCode: number }>) => {
      const statusCode = err.response?.data?.statusCode || err.response?.status;
      const message = err.response?.data?.message || err.message;
      if (statusCode === 422) {
        showToast(toast, 'error', message || 'Melebihi Kuantitas');
      } else if (statusCode === 404) {
        showToast(toast, 'error', message || 'Promo Tidak ditemukan');
      } else {
        showToast(toast, 'error', message || 'Gagal Update Product Highlight');
      }
    },
  });

  const handleChangeIsShowBeranda = useMutation({
    mutationFn: () =>
      toggleShowPromoHomepage(
        token,
        props.item.id,
        !props.item.is_display_on_homepage
      ),
    onSuccess: () => {
      if (!props.item.is_display_on_homepage === true) {
        showToast(toast, 'success', 'Berhasil update tampil di Banner Beranda');
      } else {
        showToast(
          toast,
          'success',
          'Berhasil update tidak tampil di Banner Beranda'
        );
      }
    },
    onError: (err) => {
      const error = err.message.substring(err.message.indexOf('422'));
      if (error === '422') {
        return showToast(
          toast,
          'error',
          'Jumlah banner promo sudah melebihi batas maksimum yang diizinkan. Batas maksimum adalah 12 banner'
        );
      }
      showToast(toast, 'success', err.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [BACKOFFICE_GROUP_PROMO_QUERY_KEY],
      });
    },
  });

  const handleChangeStatusInHomePage = useMutation({
    mutationFn: () =>
      updatePromoDetail(token, props.item.id, {
        is_enabled: !props.item.is_enabled,
        promo_name: props.item.promo_name,
        banner_url: props.item.banner_url,
        terms_and_conditions: props.item.terms_and_conditions || '',
      }),
    onSuccess: () => {
      if (!props.item.is_enabled === true) {
        showToast(toast, 'success', 'Berhasil update tampil di SOL');
      } else {
        showToast(toast, 'success', 'Berhasil update tidak tampil di SOL');
      }
    },
    onError: (err) => {
      showToast(toast, 'error', err.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [BACKOFFICE_GROUP_PROMO_QUERY_KEY],
      });
    },
  });

  const handletStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Aktif';
      case 'inactive':
        return 'Tidak Aktif';
      default:
        return 'Belum Aktif';
    }
  };

  const handleColorDotsStatus = (status: BackofficePromoQuery) => {
    switch (status) {
      case 'active':
        return '#00A34B';
      case 'inactive':
        return '#9747FF';

      default:
        return '#9747FF';
    }
  };

  const handlePromoType = (promo_type: string) => {
    switch (promo_type) {
      case 'free_goods':
        return 'Free Goods';
      case 'discounts':
        return 'Discounts';
      default:
        return '-';
    }
  };
  const handleSaveHighlight = (value: number) => {
    handleUpdateHighlight.mutate(value);
  };

  return (
    <Flex alignItems="center" maxW="full" overflowX="auto" w="full">
      <Flex
        alignItems="center"
        fontSize="sm"
        gap="2"
        w="full"
        id="container"
        justifyContent="space-between"
      >
        {/* The drag handle column  */}
        <Stack w={columnWidths.promoBanner}>
          {/* Banner Promo */}
          <Box rounded="md" h="100px">
            <AspectRatio ratio={1312 / 512} w="full" h="full">
              <ChakraImage
                rounded="md"
                w="100%"
                objectFit="cover"
                alt="logo"
                src={
                  props.item.banner_url === null
                    ? '/assets/images/admin-banner-home.png'
                    : props.item.banner_url
                }
              />
            </AspectRatio>
          </Box>
        </Stack>
        <Stack w={columnWidths.brand} textAlign="center">
          {/* Brand */}
          <Link
            href={detailLink}
            wordBreak="break-all"
            textDecoration="ActiveBorder"
          >
            {props.item.promo_name || '-'}
          </Link>
        </Stack>
        <Tooltip label={props.item.mechanism}>
          {/* Mekanisme */}
          <Stack
            w={columnWidths.mechanism}
            fontWeight="medium"
            textAlign="center"
          >
            <Text noOfLines={2}>{props.item.mechanism || '-'}</Text>
          </Stack>
        </Tooltip>
        <Stack w={columnWidths.period} textAlign="center">
          {/* Periode */}
          <Text fontSize="sm">
            {promoValidFromToFormat(
              props.item.valid_from,
              props.item.valid_until
            )}
          </Text>
        </Stack>
        <Stack w={columnWidths.code} textAlign="center">
          {/* Kode */}
          <Link
            href={`/promo/${props.item.id}`}
            wordBreak="break-all"
            textDecoration="ActiveBorder"
          >
            {props.item.group_id || '-'}
          </Link>
        </Stack>
        <Stack w={columnWidths.type} textAlign="center">
          {/* Jenis */}
          <Link
            href={`/promo/${props.item.id}`}
            wordBreak="break-all"
            textDecoration="ActiveBorder"
            textTransform="capitalize"
          >
            {handlePromoType(props.item.promo_type)}
          </Link>
        </Stack>
        <Stack w={columnWidths.productHighlight} textAlign="center">
          {/* Product Highlight */}
          <Stack w="full">
            <ProductHighlightInput
              control={control}
              name={`highlight-${props.item.id}`}
              defaultValue={props.item.product_highlight_number || 0}
              isUpdateDisabled={isDisabled}
              updateMutation={handleUpdateHighlight}
              onSaveHighlight={handleSaveHighlight}
            />
          </Stack>
        </Stack>
        <Stack w={columnWidths.status} textAlign="center">
          {/* Status */}
          <Flex alignItems="center" gap={2}>
            <Box
              mt="1px"
              width="10px"
              flexShrink={0}
              height="10px"
              backgroundColor={handleColorDotsStatus(props.item.status)}
              borderRadius="50%"
            />
            <Text>{handletStatusLabel(props.item.status)}</Text>
          </Flex>
        </Stack>
        <Stack w={columnWidths.displayInSOL} textAlign="center">
          <Flex gap={3} alignItems="center">
            {/* Tampil Di SOL */}
            <Text opacity={isDisabled ? 0.5 : 1}>
              {props.item.is_enabled === true ? 'Ya' : 'Tidak'}
            </Text>

            <Switch
              size="md"
              bgColor="white"
              colorScheme="linkedin"
              name="show-in-sol"
              data-testid="show-in-sol"
              isDisabled={isDisabled}
              isChecked={props.item.is_enabled === true}
              onChange={() => {
                handleChangeStatusInHomePage.mutate();
                if (props.item.is_display_on_homepage === true) {
                  return handleChangeIsShowBeranda.mutate();
                }
              }}
            />
          </Flex>
        </Stack>
        <Stack w={columnWidths.homeBanner} textAlign="center">
          <Flex gap={3} alignItems="center">
            {/* Banner Beranda */}
            <Text opacity={isDisabled ? 0.5 : 1}>
              {props.item.is_display_on_homepage ? 'Ya' : 'Tidak'}
            </Text>

            <Switch
              size="md"
              bgColor="white"
              colorScheme="linkedin"
              name="show-in-beranda"
              data-testid="show-in-beranda"
              isChecked={props.item.is_display_on_homepage}
              disabled={props.item.is_enabled === false || isDisabled}
              onChange={() => handleChangeIsShowBeranda.mutate()}
            />
          </Flex>
        </Stack>
      </Flex>
    </Flex>
  );
}
